<template>
  <template v-if="store.isLoaded && store.canRespond && hasSection">
    <p v-if="sectionCaption" class="mpf-caption-m">{{ sectionCaption }}</p>

    <mpf-form
      :key="sectionSlug ?? 'single'"
      :fields="fields"
      :form-errors="store.formErrors"
      :submit-label="submitLabel"
      @submit="handleSubmit"
    />
  </template>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import { router } from '../../router';
  import { useQuestionnaireStore } from '../../stores/useQuestionnaireStore';
  import { useErrorStore } from '../../stores/useErrorStore';
  import { useRouteMetaOverrideStore } from '../../stores/useRouteMetaOverrideStore';
  import type { FormFieldValue } from '../components/MpfForm/MpfForm.props';

  const route = useRoute();
  const store = useQuestionnaireStore();
  const errorStore = useErrorStore();
  const metaOverride = useRouteMetaOverrideStore();

  const tenantId = computed(() => String(route.params.tenantId ?? ''));
  const requestId = computed(() => String(route.params.requestId ?? ''));
  // Built with the tenant explicitly rather than leaning on patchTenantAwarePush,
  // which needs the tenant store loaded to prefix anything.
  const questionnairePath = (suffix: string) =>
    `/${tenantId.value}/questionnaire/${requestId.value}/${suffix}`;
  const sectionSlug = computed(() => (route.params.section as string | undefined) ?? null);

  const hasSection = computed(() => store.sectionForSlug(sectionSlug.value) !== null);
  const fields = computed(() => store.fieldsForSection(sectionSlug.value));
  const sectionCaption = computed(
    () => (store.isSectioned && store.sectionForSlug(sectionSlug.value)?.caption) || null,
  );
  const submitLabel = computed(() =>
    store.isLastSection(sectionSlug.value) ? 'Submit questionnaire' : 'Continue',
  );

  async function loadQuestionnaire() {
    if (!requestId.value) return;

    const outcome = await store.load(requestId.value);

    if (outcome.ok) {
      // The template names the page — one h1, rendered by the layout.
      metaOverride.setOverrides({ layoutPageTitle: store.title, pageTitle: store.title });

      return;
    }

    if (outcome.reason === 'unauthenticated') {
      await router.push(questionnairePath('verify-identity'));
      return;
    }

    if (outcome.reason === 'not-found' || outcome.reason === 'failed') {
      errorStore.setErrors([{ code: outcome.code ?? 'unknown-error' }]);
      await router.push(`/${tenantId.value}/error`);
      return;
    }

    if (outcome.reason === 'cannot-respond') {
      // The endpoint names the reason as a code, so use it rather than assuming
      // already-completed is the only one it can produce.
      errorStore.setErrors([{ code: outcome.code ?? 'questionnaire-already-completed' }]);
      await router.push(`/${tenantId.value}/error`);
    }
  }

  const handleSubmit = async (data: { values: Record<string, FormFieldValue>; valid: boolean }) => {
    if (!data.valid) return;

    store.setAnswers(data.values);

    // Sections are a rendering split, not separate submissions — only the last
    // one files the response.
    const next = store.nextSectionSlug(sectionSlug.value);
    if (next) {
      await router.push(questionnairePath(`form/${next}`));
      return;
    }

    const result = await store.submit();
    if (!result.ok) return;

    await router.push(questionnairePath('submitted-successfully'));
  };

  onUnmounted(() => {
    metaOverride.clearOverrides();
  });

  onMounted(() => {
    if (!store.isLoaded || store.requestId !== requestId.value) {
      loadQuestionnaire();
    }
  });

  // A refresh straight onto a section URL, or a Back out of one, still needs the
  // template in hand before it can render.
  watch(requestId, (id, previous) => {
    if (id && id !== previous) loadQuestionnaire();
  });

  // A slug this template has no section for would otherwise render an empty form
  // with a live submit button. Watched rather than checked on load, so it also
  // covers navigating to one once the template is already in hand.
  watch(
    [() => store.isLoaded, sectionSlug],
    ([loaded]) => {
      if (loaded && sectionSlug.value && !hasSection.value) {
        router.replace(questionnairePath('form'));
      }
    },
    { immediate: true },
  );
</script>
